// Gloves roster for the admin. Words: GLOVES_WORDS.md "Admin roster" (approved 2026-10-05),
// read with GLOVES_REWORK_WORDS.md (approved 2026-10-06): no Swap, no cuff photo, no retest.
// Each row uses the same card the QEW sees (gloveCardOf), so the two never disagree.
// Pure: no database. Shared by the page and the unit tests.

import { APP_STAMP, gloveCardOf, pairOf, statusOf, usDate, type GloveCard, type GlovePair, type LastInspection, type RetiredNote } from "./gloves";

/** gloves/{pairId}, as stored. */
export type GloveDoc = Record<string, unknown> & { pairId: string };
/** gloveInspections/{id}, as the JSP will write it (Stage 4). */
export type InspectionDoc = { qewId: string; pairId: string; inspectedAt: string; jspNumber?: string | null; isPassed?: boolean };

export type RosterBucket = "expired" | "soon" | "none" | "current";

export const BUCKETS: { key: RosterBucket; label: string; tone: "bad" | "warn" | "wait" | "ok" }[] = [
  { key: "current", label: "Current", tone: "ok" },
  { key: "soon", label: "Due Soon", tone: "warn" },
  { key: "expired", label: "Expired", tone: "bad" },
  { key: "none", label: "No Gloves", tone: "wait" },
];

export interface RosterRow {
  qewId: string;
  name: string;
  siteNumber: string;
  /** Gloves open after Nodes 1 to 3 (GATES_AT_CUTOVER_WORDS.md). */
  locked: boolean;
  card: GloveCard | null;
  bucket: RosterBucket;
  pairId: string | null;
  /** "Replace by" or "Use by" date, with days left or over. */
  dueLine: string;
  lastInspection: LastInspection | null;
}

export interface PersonGloves {
  pair: GlovePair | null;
  lastRetired: RetiredNote | null;
  pastPairs: { pairId: string; removedOn: string; removalReason: string; byApp: boolean }[];
  inspections: InspectionDoc[];
}

/** One QEW's pairs and inspections, as the Dashboard reads them. */
export function personGloves(qewId: string, gloves: GloveDoc[], inspections: InspectionDoc[]): PersonGloves {
  const mine = gloves.filter((g) => g.qewId === qewId);
  const active = mine.find((g) => statusOf(g.status) !== "retired");
  const retired = mine
    .filter((g) => statusOf(g.status) === "retired")
    .map((g) => ({
      pairId: g.pairId,
      removedOn: String(g.expiredOn ?? g.removedOn ?? ""),
      removalReason: String(g.removalReason ?? "Retired"),
      byApp: g.removedBy === APP_STAMP,
      at: String(g.removedAt ?? g.removedOn ?? ""),
    }))
    .sort((a, b) => b.at.localeCompare(a.at));
  const last = retired.find((r) => r.byApp && r.removalReason.startsWith("Expired"));
  return {
    pair: active ? pairOf(active.pairId, active) : null,
    lastRetired: last ? { pairId: last.pairId, removedOn: last.removedOn, removalReason: last.removalReason } : null,
    pastPairs: retired.map(({ at: _at, ...r }) => r),
    inspections: inspections.filter((x) => x.qewId === qewId).sort((a, b) => b.inspectedAt.localeCompare(a.inspectedAt)),
  };
}

function lastInspectionOf(pg: PersonGloves): LastInspection | null {
  if (!pg.pair) return null;
  const x = pg.inspections.find((i) => i.pairId === pg.pair!.pairId);
  return x ? { inspectedOn: x.inspectedAt.slice(0, 10), jspNumber: x.jspNumber ?? null } : null;
}

/** Which count a card falls under. A sealed pair in hand is Current; a red line after expiry is Expired. */
export function bucketOf(card: GloveCard | null): RosterBucket {
  if (!card) return "none";
  if (card.dashTone === "bad") return "expired";
  if (card.dashTone === "warn") return "soon";
  if (card.state === "pickSize" || card.state === "waiting") return "none";
  return "current";
}

const ORDER: Record<RosterBucket, number> = { expired: 0, soon: 1, none: 2, current: 3 };

export interface RosterInput {
  qewId: string;
  name: string;
  siteNumber: string;
  gloveSize: string | null;
  nodesDone: boolean;
}

/** Every Active QEW at the sites given, worst first, then by ID. */
export function rosterRows(people: RosterInput[], gloves: GloveDoc[], inspections: InspectionDoc[], today: string): RosterRow[] {
  return people
    .map((p) => {
      const pg = personGloves(p.qewId, gloves, inspections);
      const last = lastInspectionOf(pg);
      const card = p.nodesDone || pg.pair ? gloveCardOf({ gloveSize: p.gloveSize, pair: pg.pair, lastInspection: last, lastRetired: pg.lastRetired }, today) : null;
      const row: RosterRow = {
        qewId: p.qewId,
        name: p.name,
        siteNumber: p.siteNumber,
        locked: !p.nodesDone && !pg.pair,
        card,
        bucket: bucketOf(card),
        pairId: pg.pair?.pairId ?? null,
        dueLine: dueLineOf(card),
        lastInspection: last,
      };
      return row;
    })
    .sort((a, b) => ORDER[a.bucket] - ORDER[b.bucket] || a.qewId.localeCompare(b.qewId, "en", { numeric: true }));
}

function dueLineOf(card: GloveCard | null): string {
  if (!card) return "";
  const r = card.rows.find((x) => x.label === "Replace by" || x.label === "Use by" || x.label === "Was good until");
  return r ? `${r.label} ${r.value}` : "";
}

/** Every Active QEW badge, with the person's name and size. The admin badge is left out. Shared by Gloves and Admin Home. */
export function rosterForData(
  d: {
    badges: { "Badge ID": string; "Holder Name"?: string; Status?: string }[];
    people: { qewId: string; name?: string; gloveSize?: string | null }[];
    nodesDone: ReadonlySet<string>;
    gloves: GloveDoc[];
    inspections: InspectionDoc[];
  },
  today: string,
): RosterRow[] {
  const people = new Map(d.people.map((p) => [p.qewId, p]));
  const input = d.badges
    .filter((b) => b.Status === "Active" && /^\d{2}-QEW\d{2}$/.test(b["Badge ID"]))
    .map((b) => {
      const id = b["Badge ID"];
      const p = people.get(id);
      return { qewId: id, name: p?.name || b["Holder Name"] || id, siteNumber: id.slice(0, 2), gloveSize: p?.gloveSize ?? null, nodesDone: d.nodesDone.has(id) };
    });
  return rosterRows(input, d.gloves, d.inspections, today);
}

export function bucketCounts(rows: RosterRow[]): Record<RosterBucket, number> {
  const out: Record<RosterBucket, number> = { current: 0, soon: 0, expired: 0, none: 0 };
  for (const r of rows) out[r.bucket] += 1;
  return out;
}

/** The status word a row shows: the card title, or Gloves Locked before Nodes 1 to 3. */
export function rowStatus(r: RosterRow): string {
  if (r.locked) return "Gloves Locked";
  return r.card?.title ?? "No Gloves";
}

export function pastPairLine(p: PersonGloves["pastPairs"][number]): string {
  return `${p.pairId} · ${p.removalReason}${p.removedOn ? ` · ${usDate(p.removedOn)}` : ""}${p.byApp ? " · by SafeTag" : ""}`;
}
